import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import XIcon from './icons/XIcon';
import type { ConsultantPhrase, Language, User } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import {
    isPhraseActive,
    setConsultantPhraseOfTheDay,
    subscribeConsultantPhraseHistory,
    subscribeCurrentConsultantPhrase,
    updateCurrentConsultantPhrase,
    PHRASE_VALIDITY_DAYS,
} from '../services/consultantPhraseService';
import { formatRelativeTime } from '../utils/timeAgo';

interface ConsultantPhraseAdminModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
}

const ConsultantPhraseAdminModal: React.FC<ConsultantPhraseAdminModalProps> = ({ isOpen, onClose, user }) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language, key);
    const rtl = isRTL(user.language);
    const [currentPhrase, setCurrentPhrase] = useState<ConsultantPhrase | null>(null);
    const [history, setHistory] = useState<ConsultantPhrase[]>([]);
    const [draft, setDraft] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        const unsubCurrent = subscribeCurrentConsultantPhrase(user.uid, (phrase) => {
            setCurrentPhrase(phrase);
            setDraft(phrase?.phrase ?? '');
        });
        const unsubHistory = subscribeConsultantPhraseHistory(user.uid, setHistory);
        return () => {
            unsubCurrent();
            unsubHistory();
        };
    }, [isOpen, user.uid]);

    const pastPhrases = useMemo(
        () => history.filter((entry) => !entry.isCurrent || !isPhraseActive(entry)),
        [history],
    );

    if (!isOpen) return null;

    const handleSaveCurrent = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        try {
            if (currentPhrase && isPhraseActive(currentPhrase)) {
                await updateCurrentConsultantPhrase(currentPhrase.id, draft);
            } else {
                await setConsultantPhraseOfTheDay(user.uid, draft);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoading(false);
        }
    };

    const handleSetNew = async () => {
        setLoading(true);
        setError('');
        try {
            await setConsultantPhraseOfTheDay(user.uid, draft);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[220] flex items-center justify-center p-4" onClick={onClose}>
            <div
                dir={rtl ? 'rtl' : 'ltr'}
                className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col border border-slate-200 dark:border-slate-700"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('phraseOfTheDayAdmin')}</h2>
                        <p className="text-xs text-slate-400 mt-1">{t('phraseOfTheDayHint').replace('{days}', String(PHRASE_VALIDITY_DAYS))}</p>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 overflow-y-auto space-y-5 flex-1">
                    <form onSubmit={handleSaveCurrent} className="space-y-3">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
                            {t('phraseOfTheDayCurrent')}
                        </label>
                        <textarea
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            rows={3}
                            className="w-full p-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm"
                            placeholder={t('phraseOfTheDayPlaceholder')}
                        />
                        {currentPhrase && isPhraseActive(currentPhrase) && (
                            <p className="text-[10px] text-slate-400">
                                {t('phraseOfTheDayExpires')}: {new Date(currentPhrase.expiresAt).toLocaleDateString(user.language === 'he' ? 'he-IL' : undefined)}
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="submit"
                                disabled={loading || !draft.trim()}
                                className="px-4 py-2 rounded-xl bg-brand-sage text-white text-xs font-black disabled:opacity-50"
                            >
                                {currentPhrase && isPhraseActive(currentPhrase) ? t('save') : t('phraseOfTheDaySet')}
                            </button>
                            {currentPhrase && isPhraseActive(currentPhrase) && (
                                <button
                                    type="button"
                                    disabled={loading || !draft.trim()}
                                    onClick={handleSetNew}
                                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-black disabled:opacity-50"
                                >
                                    {t('phraseOfTheDayPublishNew')}
                                </button>
                            )}
                        </div>
                    </form>

                    <div>
                        <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                            {t('phraseOfTheDayHistory')}
                        </h3>
                        <div className="space-y-2">
                            {pastPhrases.length === 0 ? (
                                <p className="text-sm text-slate-400">{t('phraseOfTheDayHistoryEmpty')}</p>
                            ) : (
                                pastPhrases.map((entry) => (
                                    <div
                                        key={entry.id}
                                        className="rounded-2xl border border-slate-100 dark:border-slate-800 p-3 bg-slate-50/70 dark:bg-slate-800/40"
                                    >
                                        <p className="text-sm italic text-slate-600 dark:text-slate-300">“{entry.phrase}”</p>
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            {formatRelativeTime(entry.createdAt, user.language)}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {error && <p className="px-5 pb-4 text-sm text-red-600">{error}</p>}
            </div>
        </div>,
        document.body,
    );
};

export default ConsultantPhraseAdminModal;
